import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, UploadFile, message } from 'antd';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import cloudIcon from 'utils/icon/icon_cloud-upload-image-black.svg';
import deleteIcon from 'utils/icon/icon_close-black.svg';
import loadingIcon from 'utils/icon/icon_spinner_black.svg';
import { handleMessage } from 'utils/helper/message';

export interface IProps {
  zip: UploadFile | null;
  setZip: React.Dispatch<React.SetStateAction<UploadFile | null>>;
  requiredSubtitle?: ReactNode;
  placeholder?: string;
  name?: null | string;
}

const UploadZip: React.FC<IProps> = ({
  zip,
  setZip,
  requiredSubtitle,
  placeholder = 'updateStory.form.placeholder',
  name = null,
}) => {
  const { t } = useTranslation();

  const handleRemoveImage = () => {
    setZip(null);
  };
  const props = {
    name: 'file',
    action: 'abc',
    showUploadList: false,
    beforeUpload: (file) => {
      const zip = file.type === 'application/x-zip-compressed';

      if (zip) {
        if (file.size > 8000000) {
          handleMessage(
            message,
            'error',
            'Upload failed. Max size for zip is 8 MB.',
          );
        } else {
          setZip(file);
        }
      } else {
        handleMessage(message, 'error', `${file.name} is not a zip`);
      }
      return false;
    },
  };

  function getFileName(filePath: string) {
    return filePath?.substr(filePath.lastIndexOf('\\') + 1).split('.')[0];
  }

  const renderListImageItem = () => {
    return (
      <>
        {zip ? (
          <ListImageWrapper>
            <p>{getFileName(zip.name)}</p>
            {zip.size && <p>{(zip.size / (1024 * 1024)).toFixed(1)}MB</p>}
            {zip.status === 'uploading' && (
              <CustomLoadingIndicator src={loadingIcon} />
            )}
            <img
              src={deleteIcon}
              alt='icon-delete'
              onClick={handleRemoveImage}
            />
          </ListImageWrapper>
        ) : (
          <></>
        )}
      </>
    );
  };

  return (
    <UploadWrapper>
      <div className='uploadTotalWrap'>
        <p className='name'>{name && name}</p>
        <Upload {...props} maxCount={1}>
          <div className='emptyImage'>
            <img src={cloudIcon} alt='upload cloud' />
            <p className='emptyImageText'>{t(placeholder)}</p>
          </div>
        </Upload>
        {renderListImageItem()}
        {!requiredSubtitle ? (
          <p className='turText'>{t('updateStory.form.required')}</p>
        ) : (
          requiredSubtitle
        )}
      </div>
    </UploadWrapper>
  );
};

export default UploadZip;

const UploadWrapper = styled.div`
  display: flex;
  .ant-upload,
  .ant-upload-wrapper {
    width: 100%;
  }
  .emptyImage {
    height: 144px;
    border: 1px solid ${theme.colors.dark_100};
    border-radius: 4px;
    border-style: dotted;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px;
    & > * + * {
      margin-top: 10px;
      max-width: 145px;
      text-align: center;
    }
    cursor: pointer;
  }
  .turText {
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    margin-top: 8px;
  }
  .emptyImageText {
    color: ${theme.colors.dark_500};
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }
  .uploadTotalWrap {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  & > * + * {
    /* margin-left: 24px; */
    /* column-gap: 24px; */
  }
  .name {
    font-size: ${theme.fontSizes.base};
    font-style: normal;
    font-weight: ${theme.fontWeights.bold};
    text-align: center;
  }
`;

const ListImageWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-top: 8px;
  background-color: ${theme.colors.aliceBlue};
  border-radius: 4px;
  p {
    font-size: ${theme.fontSizes.small};
    font-weight: ${theme.fontWeights.medium};
    line-height: 20px;
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_500};
  }
  p:nth-child(2n) {
    color: ${theme.colors.dark_300};
    padding-left: 8px;
  }
  img {
    margin-left: auto;
    cursor: pointer;
  }
`;

const CustomLoadingIndicator = styled.img``;
